import { useCallback, useEffect, useState } from 'react';
import type { Lang } from '../data/quizzes';

const KEY = 'quiz-lang-v1';

function readLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'ru' || saved === 'en') return saved;
    return navigator.language?.toLowerCase().startsWith('ru') ? 'ru' : 'en';
  } catch {
    return 'ru';
  }
}

/** Язык сайта (переводит шапку и контент квизов): localStorage + синхронизация между вкладками/страницами. */
export function useQuizLang(): [Lang, (next: Lang) => void] {
  const [lang, setLangState] = useState<Lang>(readLang);

  useEffect(() => {
    const sync = () => setLangState(readLang());
    window.addEventListener('storage', sync);
    window.addEventListener('quiz-lang-change', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('quiz-lang-change', sync);
    };
  }, []);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* приватный режим — просто держим язык в памяти */
    }
    window.dispatchEvent(new Event('quiz-lang-change'));
  }, []);

  return [lang, setLang];
}

export const QUIZ_TEXT = {
  ru: {
    all: 'Все',
    crumb: (n: number) => `/ Квизы · соло-режим · ${n} штук`,
    titleA: 'Проверь',
    titleB: 'эрудицию',
    lede: (n: number) =>
      `${n} квизов по тому, во что играет весь интернет: кино, музыка, мемы, девяностые и немного хардкора. Отвечай сразу — очки копятся, факты прилагаются.`,
    found: 'Найдено',
    sort: 'Сортировка: популярные',
    questions: (n: number) => `${n} вопросов`,
    play: 'Играть →',
    back: '← Все квизы',
    notFoundLabel: '/ 404',
    notFound: 'Квиз не найден',
    notFoundText: (n: number) => `Такого квиза пока нет — но есть ещё ${n} других.`,
    allQuizzes: 'Все квизы →',
    record: (n: number) => `рекорд ${n}%`,
    resultLabel: '/ Результат',
    correctLine: (score: number, total: number) => `Верных ответов: ${score} из ${total}`,
    bestLine: (n: number) => `личный рекорд: ${n}%`,
    again: 'Ещё раз',
    nextQuiz: 'Следующий',
    question: (i: number, total: number) => `Вопрос ${i} / ${total}`,
    toResults: 'К результатам →',
    next: 'Дальше →',
    langLabel: 'Язык сайта',
    battleCta: 'Квиз-баттл с телефона →',
    battleSub: 'Сборки по темам, 3–4 уровня, вход по коду, аватары и статистика между уровнями.',
    packs: {
      crumb: (n: number) => `/ Квиз-баттл · мультиплеер · ${n} сборок`,
      titleA: 'Квиз-баттл',
      titleB: 'с телефона',
      lede: 'Выбери сборку по теме — внутри 3–4 уровня от простого к сложному. Создай комнату: игроки зайдут по коду с телефона, назовутся как захотят и выберут аватар. Между уровнями — таблица результатов.',
      levels: (n: number) => `${n} уровня`,
      create: 'Создать комнату →',
      loadError: 'Не удалось загрузить сборки. Обновите страницу.',
    },
    verdicts: [
      { min: 90, title: 'Ходячая энциклопедия', text: 'Такие результаты видит один из ста. Скринь и кидай в чат.' },
      { min: 70, title: 'Крепкий уровень', text: 'Есть куда расти, но сегодня ты явно умнее середины комнаты.' },
      { min: 50, title: 'Середняк-выживальщик', text: 'Половина — уже неплохо. Осталось добить детали.' },
      { min: 30, title: 'Внимание, эрудиция кончается', text: 'Зато ты честно дошёл до конца. Вторая попытка дешевле первой.' },
      { min: 0, title: 'Гугл тревожится о тебе', text: 'Кажется, вопросы были коварнее, чем казались. Ещё раз?' },
    ],
  },
  en: {
    all: 'All',
    crumb: (n: number) => `/ Quizzes · solo mode · ${n} of them`,
    titleA: 'Test your',
    titleB: 'trivia brain',
    lede: (n: number) =>
      `${n} quizzes about everything the internet plays with: movies, music, memes, the nineties and a bit of hardcore. Answer on the spot — points add up, facts included.`,
    found: 'Found',
    sort: 'Sorted by: popular',
    questions: (n: number) => `${n} questions`,
    play: 'Play →',
    back: '← All quizzes',
    notFoundLabel: '/ 404',
    notFound: 'Quiz not found',
    notFoundText: (n: number) => `No such quiz yet — but there are ${n} others waiting.`,
    allQuizzes: 'All quizzes →',
    record: (n: number) => `best ${n}%`,
    resultLabel: '/ Result',
    correctLine: (score: number, total: number) => `Correct answers: ${score} of ${total}`,
    bestLine: (n: number) => `personal best: ${n}%`,
    again: 'Play again',
    nextQuiz: 'Next up',
    question: (i: number, total: number) => `Question ${i} / ${total}`,
    toResults: 'See results →',
    next: 'Next →',
    langLabel: 'Site language',
    battleCta: 'Quiz battle from phones →',
    battleSub: 'Themed packs, 3–4 levels, join by code, avatars and level-by-level stats.',
    packs: {
      crumb: (n: number) => `/ Quiz battle · multiplayer · ${n} packs`,
      titleA: 'Quiz battle',
      titleB: 'from your phone',
      lede: 'Pick a themed pack — 3–4 levels inside, easy to hard. Create a room: players join from their phones by code, name themselves anything and pick an avatar. Level stats between rounds.',
      levels: (n: number) => `${n} levels`,
      create: 'Create room →',
      loadError: 'Could not load packs. Refresh the page.',
    },
    verdicts: [
      { min: 90, title: 'Walking encyclopedia', text: 'One in a hundred scores like this. Screenshot it and drop it in the chat.' },
      { min: 70, title: 'Solid run', text: 'Room to grow, but today you are clearly smarter than the median room.' },
      { min: 50, title: 'Middle-of-the-pack survivor', text: 'Half is not bad at all. Now go finish off the details.' },
      { min: 30, title: 'Trivia levels critical', text: 'At least you made it to the end. The second attempt is always cheaper.' },
      { min: 0, title: 'Google is worried about you', text: 'Turns out the questions were sneakier than they looked. One more time?' },
    ],
  },
} as const;
